import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

import TableHeaderCheckboxAll from '../../../../components/Table/CheckAll';
import DropdownRequest from '../../../../components/Dropdown/Request';

import { generateTableHead, generateMassDrops, getServicesFilterName, getSelectedServices } from '../../helpers';

const Head = ({ config, sort, values, filters, fetchTableFilters, handleSelectMass, onSortTable, onChangeCheck, onChangeSelectService }) => {
  const { t } = useTranslation();
  const { darkMode } = useSelector((state) => state.general);

  const servicesFilterName = useMemo(() => getServicesFilterName(filters.service), [filters.service]);
  const selectedServices = useMemo(
    () => getSelectedServices(config?.services, filters?.service),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, filters.service],
  );
  const headers = useMemo(() => generateTableHead(sort?.columns), [sort]);
  const headerFilters = useMemo(
    () => ({
      services: config?.services?.map((service) => ({
        ...service,
        title: `${service.title} ${service?.count !== undefined ? `(${service.count})` : ''}`,
        label: service.title,
        active: service.visibility,
      })),
      modes: config?.modes?.map((mode) => ({ ...mode, title: `${mode.title} ${mode?.count !== undefined ? `(${mode.count})` : ''}` })),
    }),
    [config],
  );

  const getSortIcon = useCallback(
    (head) => {
      if (head?.active === 'desc') {
        return faSortDown;
      }
      if (head?.active === 'asc') {
        return faSortUp;
      }
      return faSort;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headers],
  );

  const massDrop = generateMassDrops(config?.access);

  const sendSortValue = (e, item) => {
    e.preventDefault();
    onSortTable(item);
  };

  return (
    <thead>
      <tr>
        {headers.map((head) => {
          switch (head.title) {
          case 'All':
            if (massDrop.items.length === 0) {
              return null;
            }
            return (
              <TableHeaderCheckboxAll
                key={head.title}
                handleSelect={(key, title) => handleSelectMass({ key, title }, 'mass')}
                transKey="Table.MassCounterRefill"
                transValue="{{count}} tasks selected"
                values={values}
                onChangeCheck={onChangeCheck}
                accessMassActions={massDrop.items}
                submenuStatuses={massDrop.submenuItems}
              />
            );

          case 'Service':
            return (
              <th key={head.title} className="tickets__status" {...head.attrs}>
                <DropdownRequest
                  type="select"
                  title={servicesFilterName}
                  items={headerFilters?.services}
                  value={selectedServices}
                  queryRequest="service"
                  onRequestFunc={() => fetchTableFilters('services')}
                  onChangeSelect={onChangeSelectService}
                  clearData={() => true}
                  darkMode={darkMode}
                  pullRight
                  isSorted
                />
              </th>
            );

          case 'Mode':
            return (
              <th key={head.title} className="tickets__status" {...head.attrs}>
                <DropdownRequest
                  title={t('Table.Mode')}
                  items={headerFilters?.modes}
                  queryRequest="mode"
                  onRequestFunc={() => fetchTableFilters('modes')}
                  clearData={() => true}
                  darkMode={darkMode}
                  pullRight
                />
              </th>
            );

          default:
            if (head?.name) {
              return (
                <th key={head.title} className={head.class} {...head.attrs}>
                  <a href="# " onClick={(e) => sendSortValue(e, head)}>
                    {head?.title}
                    {' '}
                    <FontAwesomeIcon icon={getSortIcon(head)} />
                  </a>
                </th>
              );
            }
            return (
              <th key={head.title} className={head.class} {...head.attrs}>
                {head.title}
              </th>
            );
          }
        })}
      </tr>
    </thead>
  );
};

Head.propTypes = {
  config: PropTypes.object,
  sort: PropTypes.object,
  values: PropTypes.array,
  filters: PropTypes.object,
  fetchTableFilters: PropTypes.func,
  handleSelectMass: PropTypes.func,
  onSortTable: PropTypes.func,
  onChangeCheck: PropTypes.func,
  onChangeSelectService: PropTypes.func,
};

export default Head;
